const projectParams = [
    {
        description: "The project the user is working in (UUID). Resolve project names via the findProject tool before navigating.",
        name: "projectId",
        required: true
    },
    {
        description: "The contractor package within the project (UUID). For contractor users this comes back alongside the project_id from findProject; for org-side users ask which package or use the package context already in the current location.",
        name: "contractorPackageId",
        required: true
    }
];
// ─────────────────────────────────────────────────────────────────────────────
// The SocialPro route catalog — the "routes library" the assistant reasons over
// to decide where to send the user and what to tell them once they arrive.
//
// SINGLE SOURCE OF TRUTH. To add a navigable route:
//   1. Add one object below (url, title, description, params?).
//   2. Register the route's component in the frontend router
//      (frontend/src/routes/routes.ts) at `/<url>/:<param>...`.
//   3. If the URL doesn't follow the `/<url>/:params` convention (params in
//      a different order, extra static segments, etc.), add an entry to
//      ROUTE_TEMPLATE_OVERRIDES in frontend/src/utils/routes.ts.
//
// The url IS the URL: the frontend derives each route's path as `/<url>` plus
// one `/:param` per declared param (frontend/src/utils/routes.ts), so there is
// no separate template to write or keep in sync. `RouteId` (the union of ids)
// also derives itself from this array.
//
// Descriptions are written FOR THE LLM. Aim for 2-4 sentences per route:
//   • What the user can DO on this page (verbs).
//   • Key concepts on the page that disambiguate it from neighbours.
//   • Any pre-conditions (e.g. "must be a contractor user", "needs a report id
//     — pick one from the Monthly Reports list first").
// The model uses this text to PICK the route. It is NOT shown to the user.
//
// `userExplainer` (optional) is the user-facing copy shown verbatim in the
// chat after the frontend navigates. Use it to give the user something
// concrete and accurate about the page they just landed on — second person,
// 1-3 sentences, no jargon. When omitted the frontend falls back to a generic
// "Here's the {title} page." Populate these as you go; richer copy here is
// strictly better than relying on the LLM to paraphrase `description`.
// ─────────────────────────────────────────────────────────────────────────────
export const agentRouteCatalog = [
    // ── Organisation-level pages (no params) ───────────────────────────────
    {
        description: "The whole-organisation home: a high-level snapshot of progress across every project the organisation is involved in. Shows overall labour-hour delivery vs commitments, spend, and outstanding compliance items. Use ONLY when the user has not named a specific project — if they have, pick the per-project `dashboard` route instead. Never land a user on Organisation Dashboard as a consolation when a project name failed to resolve; ask which project they meant.",
        keywords: [
            "organisation home",
            "company-wide",
            "org dashboard",
            "all projects overview",
            "bird's-eye view"
        ],
        title: "Organisation Dashboard",
        url: "organisation-dashboard",
        userExplainer: "You're on the Organisation Dashboard — a company-wide snapshot across all your projects. Scan overall labour-hour delivery vs commitments, spend, and outstanding compliance here, then open a specific project for the detail."
    },
    {
        description: "List of every project the organisation is involved in (active and archived). Use this as the navigation hub when the user wants to drill into a specific project's packages, forecasts, reports or analytics but you don't yet know which one. If the user names a project, prefer calling findProject and navigating straight to the target page instead of dropping them here.",
        keywords: [
            "projects",
            "project list",
            "all projects",
            "open a project",
            "switch project"
        ],
        title: "Projects",
        url: "projects",
        userExplainer: "You're on the Projects list — every project your organisation runs. Click a project to open its dashboard, work packages, forecast, reports, or analytics."
    },
    {
        description: "The organisation's workforce directory: every contractor employee across the organisation, with their occupation, employment level, social factors (e.g. apprentice, indigenous, female) and which projects they are assigned to. Use this to manage employees at the org level — adding, editing, or assigning to projects. For viewing only the employees on a specific project, use the Project Employees page instead.",
        keywords: [
            "employees",
            "workforce",
            "staff",
            "people",
            "team",
            "employee directory"
        ],
        title: "Workforce",
        url: "employees",
        userExplainer: "You're on the Workforce directory — every employee across your organisation. Use the search and filters to find someone, click a row to edit their details, occupation, or social factors, or add a new employee."
    },
    {
        description: "Account and organisation settings, split into tabs. Covers the user's profile details (name, email, notification preferences), password, and — for admins — the organisation's name/ABN/contact/branding, the Admins & Permissions tab (manage which projects each standard user can access and at what level), and integrations. Use for 'change my password / email / notifications', 'update our company name / address', or 'manage user access / permissions'. Pick the `tab` value that matches the request; omit it for a generic 'open settings'.",
        keywords: [
            "settings",
            "account",
            "profile",
            "preferences",
            "notifications",
            "organisation settings",
            "permissions",
            "user access"
        ],
        params: [
            {
                description: "Which Settings tab to open. Pick the value whose keywords best match the request; omit for a generic 'open settings'.",
                kind: "query",
                name: "tab",
                required: false,
                values: [
                    {
                        description: "Organisation details: company name, ABN, contact info, and branding (admins only).",
                        keywords: ["company", "organisation", "abn", "branding", "address"],
                        value: "Account Settings"
                    },
                    {
                        description: "The current user's own profile: name, email, and notification preferences.",
                        keywords: [
                            "profile",
                            "my details",
                            "name",
                            "email",
                            "notifications"
                        ],
                        value: "Profile Settings"
                    },
                    {
                        description: "Change the current user's password.",
                        keywords: ["password", "change password", "security"],
                        value: "Password"
                    },
                    {
                        description: "Admins & Permissions: manage which projects each standard user can access and their access level (View Only, Read & Write, Project Admin) per project — the org-wide project permissions matrix. Admins only.",
                        keywords: [
                            "permissions",
                            "admins",
                            "user access",
                            "project access",
                            "who can access",
                            "manage access",
                            "project admin",
                            "grant access",
                            "teams"
                        ],
                        value: "Admins & Permissions"
                    },
                    {
                        description: "Third-party integrations for the organisation (admins only).",
                        keywords: ["integrations", "connections", "third party", "api"],
                        value: "Integrations"
                    }
                ]
            }
        ],
        title: "Settings",
        url: "settings",
        userExplainer: "You're in Settings. Use the tabs at the top to switch between your profile, password, and — if you're an admin — organisation details, Admins & Permissions, and integrations."
    },
    // ── Project / package pages (need projectId + contractorPackageId) ─────
    {
        description: "The dashboard PAGE: the single source of truth for how a specific project/package is performing right now — labour-hour commitments vs actuals, current-month forecast, spend progress, document compliance status, and outstanding monthly reports. Pick this route when the user explicitly asks to OPEN / GO TO / SHOW the dashboard, OR asks a holistic PERFORMANCE / PROGRESS question with no specific number — 'how is <project> performing / doing / tracking / going against targets?' — because the full picture (including environmental, documents and reports) lives here; navigate and tell them they can check performance on the dashboard. Do NOT pick it for a 'tell me about / summarise / what is <project> / what targets does it have' PROFILE question (→ getProjectOverview, answered in chat) or for a SPECIFIC figure/number (→ queryMetrics, answered in chat). Also avoid it when the user explicitly asks for an analytics drill-down (spend / hours / docs / environmental) or a printable/exportable report.",
        keywords: [
            "project dashboard",
            "project overview",
            "project performance",
            "project status",
            "project snapshot",
            "project summary",
            "how is the project doing",
            "how is the project tracking",
            "headline status"
        ],
        params: projectParams,
        title: "Project Dashboard",
        url: "dashboard",
        userExplainer: "You're on the Project Dashboard — the headline view of how this package is tracking: labour-hour commitments vs actuals, the current-month forecast, spend progress, document compliance, and any outstanding reports. Use the tabs to jump into Labour Hours, Social Spend, or Local Content."
    },
    {
        description: "Edit the package's monthly labour-hour forecast. The user enters projected hours per target per month; ratio/moving-target commitments are entered and shown as a percentage (%) rather than raw hours. The chart shows actuals as a solid line and the forecast as a dotted line continuing from the last actual data point. Use this for any 'update the projection', 'change projected hours', or 'plan the next few months' request.",
        keywords: [
            "forecast",
            "projected hours",
            "monthly forecast",
            "projection",
            "plan",
            "future hours"
        ],
        params: projectParams,
        title: "Forecast",
        url: "forecast",
        userExplainer: "You're on the Forecast page. Enter projected hours per target per month (ratio/moving targets are entered as a %); the chart shows actuals as a solid line and your forecast as a dotted line from the last actual point. Saving re-baselines the downstream analytics."
    },
    {
        description: "The Work Packages hub for a contractor package: manage the sub-packages (add, edit, assign work scope) AND configure each package's social procurement targets and labour-hour commitments — which targets apply (e.g. apprentices, indigenous workers, women in construction, local content) and the committed value for each (fixed hours or a ratio %). This is where commitments are set up; the forecast/actuals/analytics pages downstream all reference what's defined here. To edit a specific package's targets, open it from this list — that launches the package setup wizard. Each sub-package also gets its own monthly reporting and analytics drill-down.",
        keywords: [
            "packages",
            "sub-packages",
            "work packages",
            "subcontractor packages",
            "package list",
            "targets",
            "commitments",
            "configure targets",
            "social procurement",
            "labour hour commitments",
            "kpi"
        ],
        params: projectParams,
        title: "Packages",
        url: "packages",
        userExplainer: "You're on the Work Packages hub for this package. Add or edit sub-packages and assign work scope here, and open any package to configure its social procurement targets and labour-hour commitments (fixed hours or a ratio %) in the setup wizard."
    },
    {
        description: "The package's monthly compliance reports: a chronological list of reporting periods with their status (in progress, submitted, accepted). From here the user opens a specific month to submit a new report (labour hours, spend, documents, environmental) or to view a previously submitted one. Use this whenever the user wants to 'submit this month's report', 'check last month', or 'see what's outstanding'.",
        keywords: [
            "monthly report",
            "submit report",
            "reporting",
            "monthly submission",
            "compliance report",
            "outstanding reports"
        ],
        params: projectParams,
        title: "Monthly Reports",
        url: "monthly-reports",
        userExplainer: "You're on Monthly Reports — the list of reporting periods and their status. Open a month to submit or edit that period's report (labour hours, spend, documents, environmental), or to view one already submitted."
    },
    {
        description: "Printable/exportable consolidated report across every contractor package on the project (head contractor + subcontractors). Pick this ONLY when the user explicitly asks for a report, a consolidated/aggregated view, or something to share with a client — NOT for routine 'how is the project doing / performing / tracking' questions, which belong on the `dashboard` route. If you find yourself choosing this route because the user said 'overview' or 'performance', stop — use `dashboard` instead.",
        keywords: [
            "consolidated report",
            "aggregate report",
            "combined report",
            "project-wide report",
            "report for the client"
        ],
        params: projectParams,
        title: "Project Reports",
        url: "project-reports",
        userExplainer: "You're on Project Reports — the consolidated view across every contractor package on the project. Use it to produce or share an aggregated report for the client."
    },
    {
        description: "Read-only project Details page: contract commitments, contractor contact details, and a shared comments thread. Reached from the dashboard by shared-project (Level1Only) viewers. Use for 'project details', 'contract commitments', 'contact details', or 'project comments' requests.",
        keywords: [
            "details",
            "project details",
            "contract commitments",
            "contact details",
            "comments"
        ],
        params: projectParams,
        title: "Project Details",
        url: "project-details",
        userExplainer: "You're on Project Details — contract commitments, contractor contact details, and the shared comments thread for this project."
    },
    {
        description: "Build and download custom exports for the project in the Export Generator: PDF reports (formatted for stakeholders) and CSV files (raw data for analysis), started from a report layout, a state template or one of the project's saved templates. Use for any 'download', 'export', 'send to client' or 'generate PDF' request.",
        keywords: [
            "export",
            "download",
            "pdf",
            "excel",
            "report export",
            "generate report"
        ],
        params: projectParams,
        title: "Export Generator",
        url: "create-export",
        userExplainer: "You're in the Export Generator. Build a custom export for the project — a PDF report for stakeholders or a CSV for analysis — from a layout, a state template or a saved template."
    },
    {
        description: "Drill-down analytics for labour-hour delivery: forecast vs actual hours per social-procurement target, drillable to sub-packages and individual employees. Pick this ONLY when the user explicitly asks to analyse or drill into labour hours — e.g. 'how are we tracking on apprentice hours?', 'who's been on site this month?'. For a general 'how is the project performing?' question use `dashboard`, not this page.",
        keywords: [
            "labour hours analytics",
            "hours drill-down",
            "actual hours analysis",
            "hours per target",
            "employee hours",
            "site hours analysis"
        ],
        params: projectParams,
        title: "Labour Hours Analytics",
        url: "analytics-labour-hours",
        userExplainer: "You're on Labour Hours Analytics. Compare forecast vs actual hours per social-procurement target, then drill into sub-packages or individual employees. Use the period selector to change the reporting window."
    },
    {
        description: "Drill-down analytics for procurement spend against the package's social-procurement targets — e.g. spend with indigenous-owned, social-enterprise, or local suppliers vs the committed percentage, drillable by target and sub-package. Pick this when the user explicitly asks about spend, dollars, suppliers, or procurement — not for general project-performance questions, which belong on `dashboard`.",
        keywords: [
            "spend analytics",
            "spend analysis",
            "procurement analysis",
            "supplier spend",
            "spend drill-down",
            "dollar value analysis"
        ],
        params: projectParams,
        title: "Spend Analytics",
        url: "analytics-spend",
        userExplainer: "You're on Spend Analytics. See procurement spend against each social-procurement target (e.g. indigenous-owned, social-enterprise, local) vs the committed percentage, drillable by target and sub-package."
    },
    {
        description: "Analytics for the compliance documents and policies required on the package: which documents have been submitted, which are approved, which are overdue or outstanding. Drillable by document type and sub-package. Use for 'what documents are missing?', 'has the SWMS been uploaded?', or any 'compliance paperwork' question.",
        keywords: [
            "documents",
            "policies",
            "compliance docs",
            "governance",
            "uploaded documents",
            "outstanding documents"
        ],
        params: projectParams,
        title: "Documents Analytics",
        url: "analytics-documents",
        userExplainer: "You're on Documents Analytics. Track which required compliance documents and policies are submitted, approved, or still outstanding, drillable by document type and sub-package."
    },
    {
        description: "Compliance analytics for ONE policy or framework the project has adopted in Set-Up (e.g. Building Equality Policy, Recycled First, Local Jobs First). Shows that framework's position in one place: how many of its targets are met, close or not yet, and every hours, spend, local-content and document target it generated, with its actual against its requirement. Requires a frameworkId — only frameworks the project actually selected are valid, so resolve the name against the project's frameworks before navigating. Use for 'how are we tracking on BEP?', 'are we compliant with Recycled First?', or any question naming a named policy or framework. For a single metric across all frameworks, prefer the labour-hours, spend or documents analytics route instead.",
        keywords: [
            "compliance",
            "framework",
            "policy",
            "building equality policy",
            "bep",
            "recycled first",
            "local jobs first",
            "indigenous participation",
            "green star",
            "is rating",
            "obligations",
            "are we compliant"
        ],
        params: [
            ...projectParams,
            {
                description: "The framework's catalogue id (e.g. 'VIC-4' for Building Equality Policy, 'VIC-5' for Recycled First). Must be one of the frameworks the project has selected in Set-Up; an unknown id redirects to the project dashboard.",
                name: "frameworkId",
                required: true
            }
        ],
        title: "Compliance Analytics",
        url: "analytics-compliance",
        userExplainer: "You're on Compliance for this framework. The Overview tab summarises how many of its targets are met, close or not yet; the Targets tab lists every target it generated with its actual against its requirement."
    },
    {
        description: "Environmental analytics for the package — the entry point to per-stream sub-reports for energy, water, waste, materials and transport. Each sub-stream has its own actuals-vs-baseline trend and can be drilled down by sub-package. Use for any sustainability, emissions, resource-consumption or waste-diversion question.",
        keywords: [
            "environmental",
            "energy",
            "water",
            "waste",
            "materials",
            "transport",
            "sustainability",
            "emissions",
            "esg"
        ],
        params: projectParams,
        title: "Environmental Analytics",
        url: "analytics-environmental",
        userExplainer: "You're on Environmental Analytics — the entry point to the energy, water, waste, materials and transport sub-reports. Open a stream to see its actuals-vs-baseline trend and drill into sub-packages."
    },
    {
        description: "Analytics for Australian Industry Participation / local-content: how much of the package's spend and supply is locally sourced, measured against the committed local-content target. Drillable by sub-package and supplier. Use for AIP plan reporting or any 'local content', 'australian industry', or 'AIC' question.",
        keywords: [
            "local content",
            "australian industry participation",
            "aip",
            "aic",
            "local supply",
            "australian content"
        ],
        params: projectParams,
        title: "Local Content Analytics",
        url: "analytics-local-content",
        userExplainer: "You're on Local Content Analytics. Each card shows actual local-content spend vs the committed target for this package — click one to see the monthly trend, then drill into sub-package or supplier contribution. Use the period selector at the top to change the reporting window."
    },
    {
        description: "The employees assigned to this specific contractor package on this project — the on-site workforce for the package, with their occupations, hours, and assignment status. Use when the user wants to see 'who's on this project' or to add/remove an employee from the package's roster. For the whole-org employee directory, use the Workforce page instead.",
        keywords: [
            "project employees",
            "assigned employees",
            "package workforce",
            "on-site team",
            "who's on this project",
            "site team"
        ],
        params: projectParams,
        title: "Project Employees",
        url: "project-employees",
        userExplainer: "You're on Project Employees — the on-site workforce assigned to this package, with occupations, hours, and assignment status. Add or remove people from the package roster here."
    },
    {
        description: "The project's company directory — the only supplier directory in the app: the subcontractors and suppliers engaged on this contractor package, plus the wider SocialPro directory a head contractor can browse from here. Used to look up the procurement-relevant supplier set for this package and the spend tracked against them.",
        keywords: [
            "smart directory",
            "companies",
            "directory",
            "suppliers",
            "vendors",
            "supply chain",
            "project companies",
            "project subcontractors",
            "project suppliers",
            "engaged suppliers"
        ],
        params: projectParams,
        title: "Project Directory",
        url: "smart-directory",
        userExplainer: "You're on the Project Directory — the subcontractors and suppliers engaged on this package. Look up a supplier and the spend tracked against them here."
    },
    // ── Wizards / setup flows (single-id params) ───────────────────────────
    {
        description: "The Project Setup wizard for editing an existing project: name, dates, head-contractor details, address, and the initial set of contractor packages on the project. This is the project-level wizard; to configure a specific contractor package's social procurement targets and labour-hour commitments, use the Packages route instead. Use for 'edit project A's details', 'update the project address', or 'add a contractor package to project X'. Navigate without a projectId only when the user explicitly wants to create a NEW project.",
        keywords: [
            "project setup",
            "edit project",
            "project details",
            "project info",
            "project configuration",
            "create project"
        ],
        params: [
            {
                description: "The project to edit (UUID). Resolve from a name via findProject. Omit only when the user wants to create a brand-new project.",
                name: "projectId",
                required: false
            }
        ],
        title: "Project Setup",
        url: "project-setup",
        userExplainer: "You're in the Project Setup wizard. Edit the project's name, dates, head-contractor details, address, and its contractor packages. To configure a package's targets and commitments, use the Work Packages page instead."
    },
    {
        description: "The Employee Setup wizard for editing a contractor employee's details at the org level: identifier, occupation, employment level, social factors (apprentice, indigenous, female, etc.), training contracts, and which projects/packages they're assigned to. Use for 'edit John Smith', 'update training contract for employee X', or 'change someone's social factors'. Open without an employeeId only when creating a brand-new employee.",
        keywords: [
            "edit employee",
            "employee setup",
            "employee details",
            "training contract",
            "employee profile",
            "add employee"
        ],
        params: [
            {
                description: "The employee to edit (UUID). When the user names someone, pick them from the Workforce list (employees page) rather than guessing.",
                name: "employeeId",
                required: false
            }
        ],
        title: "Employee Setup",
        url: "employee-setup",
        userExplainer: "You're in the Employee Setup wizard. Edit this person's identifier, occupation, employment level, social factors, and training contracts, or set which projects and packages they're assigned to."
    },
    {
        description: "Open a SPECIFIC reporting month's submission wizard for a package, deep-linked straight to that month — labour hours, social spend, local content, documents and environmental for the period, ending in a Confirmation/submit step. Use this when the user names a period ('submit this month's report', 'open May's report', 'finish last month's submission') AND you can resolve the three ids. Pre-conditions: projectId + contractorPackageId (resolve via findProject / findContractorPackage or take from the current location) AND a reportId (the YYYY-MM-DD last day of the reporting month) — resolve the period with findReport FIRST; never guess the date. If you cannot pin down the month, send the user to the `monthly-reports` list instead, where they can pick it.",
        keywords: [
            "submit report",
            "submit this month",
            "monthly submission",
            "open this month's report",
            "finish report",
            "report for May",
            "edit submission"
        ],
        params: [
            {
                description: "The project (UUID). Resolve a name via findProject, or take it from the current location.",
                name: "projectId",
                required: true
            },
            {
                description: "The reporting month as a report id — the YYYY-MM-DD last day of the month (e.g. 2026-05-31). Resolve it with findReport from the user's phrasing ('this month', 'May', 'last month'); do not compute or guess it.",
                name: "reportId",
                required: true
            },
            {
                description: "The contractor package the report belongs to (UUID). Resolve via findContractorPackage, or take it from the current location.",
                name: "contractorPackageId",
                required: true
            }
        ],
        title: "Monthly Report Submission",
        url: "monthly-report-submission",
        userExplainer: "You're in the monthly report submission for the period you asked about. Work through each section — labour hours, spend, local content, documents and environmental — then submit on the final step."
    }
];
